import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import type {
    CardOperationKind,
    CustomerCardCapabilities,
    CustomerFormValues,
    CustomerFormViewMode,
    CustomerTabKey,
    OnAnalyticsEvent,
    PhoneFieldKey
} from '../types';
import type { CustomerSpdValues } from '../types/customerSpd.types';

export type CustomerFormContextType = {
    formValues: CustomerFormValues;
    // Typed as any — getFormErrorState() on the validation engine deliberately returns any.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formErrors: any;
    customerRole: CustomerTabKey;
    /** True when this form is showing an operation's draft rather than a saved participant. */
    isOperationDraft: boolean;
    handleInputChange: (field: string, value: string) => void;
    /** Coerces the raw input to a number (or undefined when empty) before setting it on the engine. */
    handleNumberInputChange: (field: string, value: string) => void;
    /** Sets the credit-consent flag on the engine — `handleInputChange` is string-only. */
    handleCreditConsentChange: (checked: boolean) => void;
    handlePhoneInputChange: (field: PhoneFieldKey, e: InterstateOnChangeEvent<string>) => void;

    // SPD values (SSN, DOB, driver's license, bank account number) — kept structurally distinct from formValues
    spdValues: CustomerSpdValues;
    /** Baseline SPD snapshot (set from the GET SPD fetch) used for SPD-specific dirty detection. */
    spdBaseline: CustomerSpdValues | null;
    /** Sets one SPD field: updates spdValues and validates via the engine's ctx.values side-channel. */
    setSpdValue: (field: string, value: string) => void;
    /** Bulk-sets SPD fields (used by useGetSpd after a fetch) and updates the SPD baseline to match. */
    setSpdValues: (values: Partial<CustomerSpdValues>) => void;
    /**
     * Bulk-updates engine values using setValues with mode: 'merge'.
     * Used by address section components for copy, swap, and add-billing operations.
     */
    handleSetValues: (values: Record<string, unknown>) => void;
    /**
     * Removes the billing address group from the engine.
     * Used by AddressCardHeader when the user deletes the billing address.
     */
    handleClearBillingAddress: () => void;
    /**
     * Initialises an empty billing address without triggering validation.
     * Used when the user clicks "Add Billing Address" so no inline errors
     * appear on the blank form before the user has entered anything.
     */
    handleInitBillingAddress: () => void;
    /** Initialises empty previous address fields without triggering validation. */
    handleInitPreviousAddress: () => void;
    /** Clears all previous address fields and triggers validation. */
    handleClearPreviousAddress: () => void;
    /** Initialises empty previous employment fields without triggering validation. */
    handleInitPreviousEmployment: () => void;
    /** Clears all previous employment fields and triggers validation. */
    handleClearPreviousEmployment: () => void;
    /** Initialises empty additional income fields without triggering validation. */
    handleInitAdditionalIncome: () => void;
    /** Clears additional income fields and triggers validation. */
    handleClearAdditionalIncome: () => void;
    isLocked: boolean;
    capabilities: CustomerCardCapabilities;
    viewMode: CustomerFormViewMode;
    /** True when the deal is in Optional Desking mode (derived from capabilities.optionalDesking). */
    isOptionalDeskingActive: boolean;
    /**
     * True when the primary buyer's customerType is 'Business'.
     * Only relevant when customerType === 'coBuyer' — controls whether Co-Applicant Type
     * or Relationship to Buyer is shown.
     */
    isBuyerBusiness: boolean;
    /** True when the customer being edited on this form has customerType === 'Business'.
     * Controls visibility of the Personal Information heading, Marital Status field,
     * and whether Title renders as a free-text input or a salutation select.
     */
    isCurrentCustomerBusiness: boolean;
    /** True when the creditAppFieldsCoverage capability is enabled (enableCreditAppFieldsCoverage toggle). */
    isCreditAppFieldsCoverageEnabled: boolean;
    enableCorrectPhoneHandling: boolean;
    /**
     * Host analytics handler. Leaf sections (e.g. CustomerSpd) call this with a neutral event name;
     * the host maps it to its own analytics dispatch. Undefined when the host omits it.
     */
    onAnalyticsEvent?: OnAnalyticsEvent;
    /** True when the buyer already has any Business/Bank field populated — drives the Business Information accordion's default-expanded state. */
    hasBusinessData: boolean;
    /**
     * Optional opt-in render-slot rendered by PersonalInfoFields beside the "Personal Information"
     * heading, buyer only (e.g. SCI's "Convert to Business" entry CTA). Undefined when the host
     * (e.g. MV) doesn't pass one — additive only.
     */
    personalInfoHeaderAction?: ReactNode;
    /** Starts an operation (card owns the unsaved-edits check); undefined if no card is hosting. */
    onRequestOperation?: (kind: CardOperationKind) => void;
    /**
     * Mirrors the UnifiedCustomerCard `hideCheckLoader` prop — true when the SPD check spinner
     * is suppressed so the form renders immediately. Threaded into context so CustomerSpd can
     * include it as an attribute on the SCI:SPDCheck NR event without prop-drilling.
     */
    hideCheckLoader?: boolean;
};

export const CustomerFormContext = createContext<CustomerFormContextType | undefined>(undefined);

export const useCustomerCardContext = (): CustomerFormContextType => {
    const context = useContext(CustomerFormContext);
    if (!context) {
        throw new Error('useCustomerCardContext must be used within a CustomerForm');
    }
    return context;
};
